/** The "Two-factor authentication" block on the profile page. Enable: enrol ->
 * scan the QR (or type the key) -> confirm a code -> save the one-time recovery
 * codes. Disable: confirm with a current code. 2FA is opt-in; there is no
 * enforcement here. */
import { useState } from "react";
import type { FormEvent } from "react";
import { disableTotp, enrollTotp, verifyTotp, type TotpEnroll } from "../../api/auth";
import { ApiError } from "../../api/http";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/ToastContext";
import { Button, Card, Input, Label } from "../../components/ui";
import { QrCode } from "../../components/QrCode";
import { useT } from "../../i18n";

type Mode = "idle" | "enrolling" | "recovery" | "disabling";

export function TwoFactorSection() {
  const t = useT();
  const { user, refresh } = useAuth();
  const { push } = useToast();
  const enabled = Boolean(user?.totp_enabled);

  const [mode, setMode] = useState<Mode>("idle");
  const [enroll, setEnroll] = useState<TotpEnroll | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEnroll = async () => {
    setBusy(true);
    setError(null);
    try {
      setEnroll(await enrollTotp());
      setCode("");
      setMode("enrolling");
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await verifyTotp(code);
      setRecovery(res.recovery_codes);
      setCode("");
      setMode("recovery");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await disableTotp(code);
      push("success", t("twofa.disabled"));
      setCode("");
      setMode("idle");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("twofa.title")}>
      {mode === "recovery" ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">{t("twofa.recoveryIntro")}</p>
          {/* mobile-ok: recovery codes are 8 characters; two columns of them
              fit a phone comfortably and keep the list half as tall. */}
          <ul className="grid grid-cols-2 gap-2 rounded-control bg-canvas p-3 font-mono text-sm">
            {recovery.map((rc) => (
              <li key={rc}>{rc}</li>
            ))}
          </ul>
          <Button onClick={() => setMode("idle")}>{t("twofa.done")}</Button>
        </div>
      ) : mode === "enrolling" && enroll ? (
        <form onSubmit={confirmEnroll} className="space-y-3">
          <p className="text-sm text-muted">{t("twofa.scanIntro")}</p>
          <QrCode text={enroll.provisioning_uri} />
          <p className="text-xs text-muted">
            {t("twofa.manualKey")} <code className="font-mono text-ink">{enroll.secret}</code>
          </p>
          <div>
            <Label htmlFor="totp_code">{t("twofa.code")}</Label>
            <Input
              id="totp_code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <Button type="submit" loading={busy}>{t("twofa.enable")}</Button>
        </form>
      ) : mode === "disabling" ? (
        <form onSubmit={confirmDisable} className="space-y-3">
          <p className="text-sm text-muted">{t("twofa.disableIntro")}</p>
          <div>
            <Label htmlFor="disable_code">{t("twofa.code")}</Label>
            <Input
              id="disable_code"
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" variant="danger" loading={busy}>{t("twofa.disable")}</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setMode("idle");
                setError(null);
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : enabled ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold text-success">✓ {t("twofa.on")}</p>
          <Button variant="secondary" onClick={() => { setCode(""); setMode("disabling"); }}>
            {t("twofa.disable")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted">{t("twofa.off")}</p>
          <Button loading={busy} onClick={startEnroll}>{t("twofa.enable")}</Button>
        </div>
      )}
    </Card>
  );
}
